import { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Plus, Trash2, Pencil, Upload, Image as ImageIcon, Search, X } from 'lucide-react';
import AdminRichTextEditor from '../components/AdminRichTextEditor.jsx';
import { normalizeQuillHtml } from '../utils/richHtml.js';
import { normalizeTeamSocialHandle } from '../utils/socialProfileUrl.js';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { PageHeader, EmptyState } from '../components/ui';
import './TeamManagement.css';
import './ModuleAdmin.css';

const TEAM_TYPES = [
  { value: 'head_of_projects', label: 'Head Of Projects' },
  { value: 'core_team', label: 'Core Team' },
  { value: 'office_team', label: 'Office Team' },
  { value: 'field_team', label: 'Field Team' },
  { value: 'volunteers', label: 'Volunteers' },
  { value: 'alumni', label: 'Alumni' }
];

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const TeamManagement = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [heroForm, setHeroForm] = useState({ title: '', subtitle: '', imageUrl: '' });
  const [sectionsForm, setSectionsForm] = useState({});
  const [members, setMembers] = useState([]);
  const [orderingType, setOrderingType] = useState('core_team');
  const [draggingId, setDraggingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [memberForm, setMemberForm] = useState({
    fullName: '', designation: '', type: 'core_team', profilePicUrl: '',
    twitter: '', facebook: '', instagram: '', linkedin: '', youtube: '', bio: '',
    isFeatured: false, order: 0, isActive: true
  });
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [memberListSearch, setMemberListSearch] = useState('');
  const profileUploading = useRef(false);
  const imageInputRef = useRef(null);
  const heroUploading = useRef(false);
  const heroImageInputRef = useRef(null);
  const { showToast } = useToast();
  const { can } = useAuth();
  const canManage = can('team_manage');

  const filteredMembersForList = useMemo(() => {
    const q = memberListSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const typeLabel = TEAM_TYPES.find((t) => t.value === m.type)?.label || m.type || '';
      const haystack = [
        m.fullName,
        m.designation,
        typeLabel,
        m.isFeatured ? 'featured' : ''
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [members, memberListSearch]);

  const { data: teamData, isLoading } = useQuery('team-admin', adminAPI.getTeam);
  const payload = teamData?.data?.data || {};

  useEffect(() => {
    if (!payload.hero && !payload.members) return;
    if (payload.hero) {
      setHeroForm({
        title: payload.hero.title || '',
        subtitle: payload.hero.subtitle || '',
        imageUrl: payload.hero.heroImageUrl || payload.hero.imageUrl || ''
      });
    }
    if (payload.members) setMembers(payload.members);
    if (payload.sectionTitles || payload.sectionIntros) {
      const next = {};
      TEAM_TYPES.forEach(({ value }) => {
        next[`team_section_${value}_title`] = payload.sectionTitles?.[value] ?? '';
        next[`team_section_${value}_intro`] = payload.sectionIntros?.[value] ?? '';
      });
      setSectionsForm(next);
    }
  }, [payload.hero, payload.members, payload.sectionTitles, payload.sectionIntros]);

  const updateHeroMutation = useMutation(
    (data) => adminAPI.updateTeamHero(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('team-admin');
        showToast({ message: 'Team hero updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update hero.', severity: 'error' })
    }
  );
  const updateSectionsMutation = useMutation(
    (data) => adminAPI.updateTeamSections(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('team-admin');
        showToast({ message: 'Team sections updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update sections.', severity: 'error' })
    }
  );
  const createMemberMutation = useMutation(
    (data) => adminAPI.createTeamMember(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('team-admin');
        setShowMemberForm(false);
        resetMemberForm();
        showToast({ message: 'Team member created.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to create member.', severity: 'error' })
    }
  );
  const updateMemberMutation = useMutation(
    ({ id, data }) => adminAPI.updateTeamMember(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('team-admin');
        setEditingId(null);
        setShowMemberForm(false);
        resetMemberForm();
        showToast({ message: 'Team member updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update member.', severity: 'error' })
    }
  );
  const deleteMemberMutation = useMutation(
    (id) => adminAPI.deleteTeamMember(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('team-admin');
        showToast({ message: 'Team member deleted.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to delete member.', severity: 'error' })
    }
  );
  const reorderMembersMutation = useMutation(
    (data) => adminAPI.reorderTeamMembers(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('team-admin');
        showToast({ message: 'Team order updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update team order.', severity: 'error' })
    }
  );

  function resetMemberForm() {
    setMemberForm({
      fullName: '', designation: '', type: 'core_team', profilePicUrl: '',
      twitter: '', facebook: '', instagram: '', linkedin: '', youtube: '', bio: '',
      isFeatured: false, order: 0, isActive: true
    });
  }

  function closeMemberForm() {
    setShowMemberForm(false);
    setEditingId(null);
    resetMemberForm();
  }

  function openEdit(member) {
    if (!canManage) {
      showToast({ message: 'You need Team – Manage permission to edit members.', severity: 'info' });
      return;
    }
    setEditingId(member.id);
    setMemberForm({
      fullName: member.fullName || '',
      designation: member.designation || '',
      type: member.type || 'core_team',
      profilePicUrl: member.profilePicUrl || '',
      twitter: normalizeTeamSocialHandle('twitter', member.twitter || ''),
      facebook: normalizeTeamSocialHandle('facebook', member.facebook || ''),
      instagram: normalizeTeamSocialHandle('instagram', member.instagram || ''),
      linkedin: normalizeTeamSocialHandle('linkedin', member.linkedin || ''),
      youtube: normalizeTeamSocialHandle('youtube', member.youtube || ''),
      bio: member.bio || '',
      isFeatured: !!member.isFeatured,
      order: member.order ?? 0,
      isActive: member.isActive !== false
    });
    setShowMemberForm(true);
  }

  function handleSaveMember() {
    if (!canManage) return;
    if (!memberForm.fullName?.trim()) {
      showToast({ message: 'Full name is required.', severity: 'warning' });
      return;
    }
    const payload = {
      ...memberForm,
      bio: normalizeQuillHtml(memberForm.bio),
      twitter: normalizeTeamSocialHandle('twitter', memberForm.twitter),
      facebook: normalizeTeamSocialHandle('facebook', memberForm.facebook),
      instagram: normalizeTeamSocialHandle('instagram', memberForm.instagram),
      linkedin: normalizeTeamSocialHandle('linkedin', memberForm.linkedin),
      youtube: normalizeTeamSocialHandle('youtube', memberForm.youtube)
    };
    if (editingId) {
      updateMemberMutation.mutate({ id: editingId, data: payload });
    } else {
      createMemberMutation.mutate(payload);
    }
  }

  async function handleProfileUpload(e) {
    if (!canManage) return;
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    profileUploading.current = true;
    try {
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url;
      if (url) setMemberForm((p) => ({ ...p, profilePicUrl: url }));
    } catch (err) {
      showToast({ message: err.response?.data?.message || 'Upload failed.', severity: 'error' });
    } finally {
      profileUploading.current = false;
      e.target.value = '';
    }
  }

  async function handleHeroImageUpload(e) {
    if (!canManage) return;
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    heroUploading.current = true;
    try {
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url;
      if (url) setHeroForm((p) => ({ ...p, imageUrl: url }));
    } catch (err) {
      showToast({ message: err.response?.data?.message || 'Upload failed.', severity: 'error' });
    } finally {
      heroUploading.current = false;
      e.target.value = '';
    }
  }

  if (isLoading) return (
    <div className="team-admin-page module-page">
      <PageHeader title="Meet Our Team" subtitle="Loading team content…" />
      <Card>
        <CardContent>
          <div className="ops-loading-box">
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const readOnlyField = !canManage;

  return (
    <div className="team-admin-page module-page">
      <PageHeader
        title="Meet Our Team"
        subtitle="Manage the public Team page: hero, section intros, and team members."
      />
      {!canManage && (
        <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 1, color: '#9a3412' }}>
          You have <strong>view-only</strong> access. Ask a super admin to grant <strong>Team – Manage</strong> on your role (Role Builder) or your user permissions so you can save changes.
        </Typography>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Hero & Sections" />
          <Tab label="Team Members" />
          <Tab label="Ordering" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <Card className="team-admin-card" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Page Hero</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Controls the top hero section on the public Teams page.
            </Typography>
            <input
              type="file"
              accept="image/*"
              ref={heroImageInputRef}
              className="team-hidden-input"
              onChange={handleHeroImageUpload}
            />
            <div className="team-form-grid">
              <TextField
                fullWidth
                label="Title"
                value={heroForm.title}
                onChange={(e) => setHeroForm((p) => ({ ...p, title: e.target.value }))}
                size="small"
                InputProps={{ readOnly: readOnlyField }}
              />
              <TextField
                fullWidth
                label="Subtitle"
                value={heroForm.subtitle}
                onChange={(e) => setHeroForm((p) => ({ ...p, subtitle: e.target.value }))}
                size="small"
                sx={{ gridColumn: '1 / -1' }}
                InputProps={{ readOnly: readOnlyField }}
              />
              <Box sx={{ gridColumn: '1 / -1' }}>
                {heroForm.imageUrl ? (
                  <div className="team-profile-preview">
                    <img src={heroForm.imageUrl} alt="" />
                    <Button
                      size="small"
                      startIcon={<Upload size={14} />}
                      onClick={() => canManage && heroImageInputRef.current?.click()}
                      disabled={!canManage}
                    >
                      Change hero image
                    </Button>
                  </div>
                ) : (
                  <div
                    className="team-profile-upload"
                    onClick={() => canManage && heroImageInputRef.current?.click()}
                    style={{ cursor: canManage ? 'pointer' : 'not-allowed', opacity: canManage ? 1 : 0.65 }}
                  >
                    <ImageIcon size={32} />
                    <span>Upload hero image</span>
                  </div>
                )}
                <TextField
                  fullWidth
                  size="small"
                  label="Or paste hero image URL"
                  value={heroForm.imageUrl}
                  onChange={(e) => setHeroForm((p) => ({ ...p, imageUrl: e.target.value }))}
                  sx={{ mt: 1 }}
                  InputProps={{ readOnly: readOnlyField }}
                />
              </Box>
            </div>
            <Button
              variant="contained"
              sx={{ mt: 1.5 }}
              onClick={() => updateHeroMutation.mutate(heroForm)}
              disabled={!canManage || updateHeroMutation.isLoading}
            >
              Save Hero
            </Button>
          </CardContent>
        </Card>
        <Card className="team-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Section Titles & Intros</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Optional title and intro text for each team category on the public page.</Typography>
            {TEAM_TYPES.map(({ value, label }) => (
              <Box key={value} sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">{label}</Typography>
                <TextField fullWidth size="small" label="Section title" value={sectionsForm[`team_section_${value}_title`] ?? ''} onChange={(e) => setSectionsForm((p) => ({ ...p, [`team_section_${value}_title`]: e.target.value }))} sx={{ mt: 0.5, mb: 1 }} InputProps={{ readOnly: readOnlyField }} />
                <TextField fullWidth size="small" multiline rows={2} label="Intro text" value={sectionsForm[`team_section_${value}_intro`] ?? ''} onChange={(e) => setSectionsForm((p) => ({ ...p, [`team_section_${value}_intro`]: e.target.value }))} InputProps={{ readOnly: readOnlyField }} />
              </Box>
            ))}
            <Button variant="contained" sx={{ mt: 1 }} onClick={() => updateSectionsMutation.mutate(sectionsForm)} disabled={!canManage || updateSectionsMutation.isLoading}>Save Sections</Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card className="team-admin-card">
          <CardContent>
            <div className="team-members-head">
              <Typography variant="subtitle1" fontWeight={600}>Team Members</Typography>
              <div className="team-members-head-actions">
                <TextField
                  size="small"
                  placeholder="Search name, role, category…"
                  value={memberListSearch}
                  onChange={(e) => setMemberListSearch(e.target.value)}
                  className="team-members-search"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} strokeWidth={2} />
                      </InputAdornment>
                    )
                  }}
                />
                <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => { setEditingId(null); resetMemberForm(); setShowMemberForm(true); }} disabled={!canManage}>Add Member</Button>
              </div>
            </div>

            <div className="team-members-list">
              {members.length === 0 && !showMemberForm && (
                <EmptyState title="No team members yet" description='Click "Add Member" to add one.' />
              )}
              {members.length > 0 && filteredMembersForList.length === 0 && (
                <EmptyState title="No matches" description={`No members match "${memberListSearch.trim()}". Try a different search.`} />
              )}
              {filteredMembersForList.map((m) => (
                <div key={m.id} className="team-member-row">
                  <div className="team-member-row-info">
                    {m.profilePicUrl ? <img src={m.profilePicUrl} alt="" className="team-member-row-avatar" /> : <div className="team-member-row-avatar team-member-row-avatar-placeholder" />}
                    <div>
                      <strong>{m.fullName}</strong>
                      {m.designation && <span className="team-member-row-role">{m.designation}</span>}
                      <span className="team-member-row-type">{TEAM_TYPES.find(t => t.value === m.type)?.label || m.type}</span>
                      {m.isFeatured && <span className="team-member-badge">Featured</span>}
                    </div>
                  </div>
                  <div className="team-member-row-actions">
                    <Button size="small" startIcon={<Pencil size={14} />} onClick={() => openEdit(m)} disabled={!canManage}>Edit</Button>
                    <Button size="small" color="error" startIcon={<Trash2 size={14} />} onClick={() => window.confirm('Delete this member?') && deleteMemberMutation.mutate(m.id)} disabled={!canManage}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Card className="team-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Reorder Team Members
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Drag members within a category to change the order they appear on the public Teams page.
              {!canManage && ' Reordering requires Team – Manage.'}
            </Typography>

            <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={orderingType}
                onChange={(e) => setOrderingType(e.target.value)}
              >
                {TEAM_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div className="team-ordering-list">
              {members.filter((m) => m.type === orderingType).length === 0 ? (
                <p className="team-no-members">No members in this category.</p>
              ) : (
                members
                  .filter((m) => m.type === orderingType)
                  .slice()
                  .sort(
                    (a, b) =>
                      (a.order ?? 0) - (b.order ?? 0) ||
                      a.fullName.localeCompare(b.fullName)
                  )
                  .map((m, index, arr) => (
                    <div
                      key={m.id}
                      className="team-ordering-row"
                      draggable={canManage}
                      onDragStart={() => canManage && setDraggingId(m.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!canManage) return;
                        if (!draggingId || draggingId === m.id) return;
                        const sameType = arr.slice(); // already sorted slice for this type
                        const fromIndex = sameType.findIndex(
                          (mm) => mm.id === draggingId
                        );
                        const toIndex = sameType.findIndex(
                          (mm) => mm.id === m.id
                        );
                        if (fromIndex === -1 || toIndex === -1) return;
                        const reordered = sameType.slice();
                        const [moved] = reordered.splice(fromIndex, 1);
                        reordered.splice(toIndex, 0, moved);

                        const current = members.slice();
                        const updated = current.map((mm) => {
                          const idx = reordered.findIndex(
                            (r) => r.id === mm.id
                          );
                          if (idx === -1) return mm;
                          return { ...mm, order: idx };
                        });
                        setMembers(updated);
                        reorderMembersMutation.mutate({
                          type: orderingType,
                          ids: reordered.map((s) => s.id)
                        });
                        setDraggingId(null);
                      }}
                      onDragEnd={() => setDraggingId(null)}
                    >
                      <span className="team-ordering-handle">☰</span>
                      <span className="team-ordering-index">{index + 1}.</span>
                      <div className="team-member-row-info">
                        {m.profilePicUrl ? (
                          <img
                            src={m.profilePicUrl}
                            alt=""
                            className="team-member-row-avatar"
                          />
                        ) : (
                          <div className="team-member-row-avatar team-member-row-avatar-placeholder" />
                        )}
                        <div>
                          <strong>{m.fullName}</strong>
                          {m.designation && (
                            <span className="team-member-row-role">
                              {m.designation}
                            </span>
                          )}
                          {m.isFeatured && (
                            <span className="team-member-badge">Featured</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </CardContent>
        </Card>
      </TabPanel>

      <Dialog
        open={Boolean(showMemberForm && tab === 1)}
        onClose={() => {
          if (createMemberMutation.isLoading || updateMemberMutation.isLoading) return;
          closeMemberForm();
        }}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="team-member-dialog-title"
        PaperProps={{ sx: { maxHeight: 'min(92dvh, 880px)' } }}
      >
        <DialogTitle
          id="team-member-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1
          }}
        >
          <span>{editingId ? 'Edit member' : 'New member'}</span>
          <IconButton
            type="button"
            aria-label="Close"
            onClick={() => {
              if (createMemberMutation.isLoading || updateMemberMutation.isLoading) return;
              closeMemberForm();
            }}
            size="small"
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <input type="file" accept="image/*" ref={imageInputRef} className="team-hidden-input" onChange={handleProfileUpload} />
          <div className="team-form-grid team-member-dialog-form">
            <TextField fullWidth label="Full Name *" value={memberForm.fullName} onChange={(e) => setMemberForm((p) => ({ ...p, fullName: e.target.value }))} size="small" InputProps={{ readOnly: readOnlyField }} />
            <TextField fullWidth label="Designation" value={memberForm.designation} onChange={(e) => setMemberForm((p) => ({ ...p, designation: e.target.value }))} size="small" placeholder="e.g. FOUNDER & MANAGING TRUSTEE" InputProps={{ readOnly: readOnlyField }} />
            <FormControl fullWidth size="small" disabled={readOnlyField}>
              <InputLabel>Type</InputLabel>
              <Select value={memberForm.type} label="Type" onChange={(e) => setMemberForm((p) => ({ ...p, type: e.target.value }))}>
                {TEAM_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ gridColumn: '1 / -1' }}>
              {memberForm.profilePicUrl ? (
                <div className="team-profile-preview">
                  <img src={memberForm.profilePicUrl} alt="" />
                  <Button size="small" startIcon={<Upload size={14} />} onClick={() => canManage && imageInputRef.current?.click()} disabled={!canManage}>
                    Change photo
                  </Button>
                </div>
              ) : (
                <div className="team-profile-upload" onClick={() => canManage && imageInputRef.current?.click()} style={{ cursor: canManage ? 'pointer' : 'not-allowed', opacity: canManage ? 1 : 0.65 }}>
                  <ImageIcon size={32} />
                  <span>Upload profile photo</span>
                </div>
              )}
              <TextField
                fullWidth
                size="small"
                label="Or paste image URL"
                value={memberForm.profilePicUrl}
                onChange={(e) => setMemberForm((p) => ({ ...p, profilePicUrl: e.target.value }))}
                sx={{ mt: 1 }}
                InputProps={{ readOnly: readOnlyField }}
              />
            </Box>
            <Box sx={{ gridColumn: '1 / -1', pointerEvents: canManage ? 'auto' : 'none', opacity: canManage ? 1 : 0.72 }}>
              <label style={{ display: 'block', fontSize: '0.82rem', marginBottom: 6, color: '#64748b' }}>Bio / Quote</label>
              <AdminRichTextEditor
                value={memberForm.bio}
                onChange={(value) => setMemberForm((p) => ({ ...p, bio: value }))}
                placeholder="Write founder/member quote or bio here..."
                hint="Supports headings, bold/italic/underline, lists, links."
              />
            </Box>
            <TextField
              fullWidth
              label="Twitter / X"
              value={memberForm.twitter}
              onChange={(e) => setMemberForm((p) => ({ ...p, twitter: e.target.value }))}
              size="small"
              placeholder="username"
              helperText="Username only (no link). Pasted URLs are saved as the handle."
              InputProps={{ readOnly: readOnlyField }}
            />
            <TextField
              fullWidth
              label="Facebook"
              value={memberForm.facebook}
              onChange={(e) => setMemberForm((p) => ({ ...p, facebook: e.target.value }))}
              size="small"
              placeholder="username or page slug"
              helperText="Username or page slug only."
              InputProps={{ readOnly: readOnlyField }}
            />
            <TextField
              fullWidth
              label="Instagram"
              value={memberForm.instagram}
              onChange={(e) => setMemberForm((p) => ({ ...p, instagram: e.target.value }))}
              size="small"
              placeholder="username"
              helperText="Username only (e.g. rushirajmori)."
              InputProps={{ readOnly: readOnlyField }}
            />
            <TextField
              fullWidth
              label="LinkedIn"
              value={memberForm.linkedin}
              onChange={(e) => setMemberForm((p) => ({ ...p, linkedin: e.target.value }))}
              size="small"
              placeholder="profile slug"
              helperText="Public profile slug from linkedin.com/in/…"
              InputProps={{ readOnly: readOnlyField }}
            />
            <TextField
              fullWidth
              label="YouTube"
              value={memberForm.youtube}
              onChange={(e) => setMemberForm((p) => ({ ...p, youtube: e.target.value }))}
              size="small"
              placeholder="@handle or channel name"
              helperText="Handle without @ is fine; links are converted when possible."
              InputProps={{ readOnly: readOnlyField }}
            />
            <TextField
              fullWidth
              type="number"
              label="Order"
              value={memberForm.order}
              onChange={(e) => setMemberForm((p) => ({ ...p, order: Number(e.target.value) || 0 }))}
              size="small"
              InputProps={{ readOnly: readOnlyField }}
            />
            <FormControlLabel
              control={
                <Checkbox checked={memberForm.isFeatured} onChange={(e) => setMemberForm((p) => ({ ...p, isFeatured: e.target.checked }))} disabled={readOnlyField} />
              }
              label="Featured (show in key leadership section)"
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" onClick={closeMemberForm} disabled={createMemberMutation.isLoading || updateMemberMutation.isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveMember} disabled={!canManage || createMemberMutation.isLoading || updateMemberMutation.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
